import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { InterferenceDetail } from '../types/Device';
import { tokenStorage } from '../services/httpClient';

interface RealtimeData {
  gnss_samples: number[] | null;
  ocxo_control: number | null;
  telemetry: {
    lock_status: string | null;
    n_gnss: number | null;
    temperature: number | null;
    interference_status?: string | null;
    interference_type?: string | null;
    interference_inputs?: number[] | null;
    interference_timestamp?: string | null;
    interference_details?: InterferenceDetail[] | null;
  } | null;
  utc_time?: string | null;
  tai_time?: string | null;
  adsb_dtoe?: number | null;
  ubx_telemetry: {
    [gnssDeviceId: number]: {
      [messageType: string]: {
        data: any;
        timestamp: string;
      };
    };
  } | null;
  lastUpdated: string | null;
}

// New interfaces for accumulated chart data
interface ChartDataPoint {
  timestamp: number;
  value: number;
}

interface GnssChartData {
  receiver1: ChartDataPoint[];
  receiver2: ChartDataPoint[];
  receiver3: ChartDataPoint[];
  receiver4: ChartDataPoint[];
}

interface RealtimeChartData {
  gnss: GnssChartData;
  ocxo: ChartDataPoint[];
}

interface DeviceGnssData {
  device_id: number;
  mqtt_id: string;
  samples: number[];
  timestamp: string;
}

interface DeviceOcxoData {
  device_id: number;
  mqtt_id: string;
  value: number;
  timestamp: string;
}

interface DeviceTelemetryData {
  device_id: number;
  mqtt_id: string;
  lock_status: string;
  n_gnss: number;
  temperature: number;
  timestamp: string;
  interference_status?: string | null;
  interference_type?: string | null;
  interference_inputs?: number[] | null;
  interference_timestamp?: string | null;
  interference_details?: any[] | null;
}

interface DeviceUbxTelemetryData {
  device_id: number;
  mqtt_id: string;
  gnss_device_id: number;
  message_type: string;
  data: any;
  timestamp: string;
}

export const useDeviceRealtime = (deviceId: number | null) => {
  const [realtimeData, setRealtimeData] = useState<RealtimeData>({
    gnss_samples: null,
    ocxo_control: null,
    telemetry: null,
    adsb_dtoe: null,
    ubx_telemetry: null,
    lastUpdated: null,
  });

  // Chart data with configurable buffer size (10 minutes of data at 1Hz)
  const MAX_CHART_POINTS = 600;
  const [chartData, setChartData] = useState<RealtimeChartData>({
    gnss: {
      receiver1: [],
      receiver2: [],
      receiver3: [],
      receiver4: [],
    },
    ocxo: [],
  });

  const socketRef = useRef<Socket | null>(null);
  const SOCKET_URL = (process.env.REACT_APP_SOCKET_URL as string) || window.location.origin;

  useEffect(() => {
    // Initialize Socket.IO connection with JWT auth
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket'],
      path: '/socket.io',
      autoConnect: !!tokenStorage.getAccess(),
      auth: (cb) => cb({ token: tokenStorage.getAccess() || '' }),
    });

    // Handle GNSS samples updates (array)
    socketRef.current.on('gnss_samples', (data: DeviceGnssData) => {
      // Only update if data is for this specific device
      if (data.device_id === deviceId) {
        const timestamp = new Date(data.timestamp).getTime();
        
        setRealtimeData(prev => ({
          ...prev,
          gnss_samples: data.samples,
          lastUpdated: data.timestamp,
        }));

        // Accumulate chart data for each GNSS receiver
        setChartData(prev => {
          const newChartData = { ...prev };
          
          // Add data points for each receiver (up to 4 receivers supported)
          data.samples.forEach((sample, index) => {
            const receiverKey = `receiver${index + 1}` as keyof GnssChartData;
            if (receiverKey in newChartData.gnss) {
              const newDataPoint: ChartDataPoint = { timestamp, value: sample };
              
              // Add new point and keep only last MAX_CHART_POINTS
              newChartData.gnss[receiverKey] = [
                ...newChartData.gnss[receiverKey],
                newDataPoint
              ].slice(-MAX_CHART_POINTS);
            }
          });

          return newChartData;
        });
      }
    });

    // Handle OCXO control updates
    socketRef.current.on('ocxo_control', (data: DeviceOcxoData) => {
      // Only update if data is for this specific device
      if (data.device_id === deviceId) {
        const timestamp = new Date(data.timestamp).getTime();
        
        setRealtimeData(prev => ({
          ...prev,
          ocxo_control: data.value,
          lastUpdated: data.timestamp,
        }));

        // Accumulate chart data for OCXO
        setChartData(prev => ({
          ...prev,
          ocxo: [
            ...prev.ocxo,
            { timestamp, value: data.value }
          ].slice(-MAX_CHART_POINTS)
        }));
      }
    });

    // Handle telemetry data updates (lock_status, n_gnss, temperature)
    socketRef.current.on('telemetry_data', (data: DeviceTelemetryData) => {
      // Only update if data is for this specific device
      if (data.device_id === deviceId) {
        setRealtimeData(prev => ({
          ...prev,
          telemetry: {
            lock_status: data.lock_status,
            n_gnss: data.n_gnss,
            temperature: data.temperature,
            interference_status: data.interference_status ?? null,
            interference_type: data.interference_type ?? null,
            interference_inputs: Array.isArray(data.interference_inputs) ? data.interference_inputs : null,
            interference_timestamp: data.interference_timestamp ?? null,
            interference_details: Array.isArray(data.interference_details) ? data.interference_details : null,
          },
          lastUpdated: data.timestamp,
        }));
      }
    });

    // Handle UTC time updates
    socketRef.current.on('utc_time', (data: { device_id?: number; mqtt_id: string; utc_time: string; timestamp: string; }) => {
      if (!deviceId || data.device_id === deviceId) {
        setRealtimeData(prev => ({
          ...prev,
          utc_time: data.utc_time,
          lastUpdated: data.timestamp,
        }));
      }
    });

    // Handle TAI time updates
    socketRef.current.on('tai_time', (data: { device_id?: number; mqtt_id: string; tai_time: string; timestamp: string; }) => {
      if (!deviceId || data.device_id === deviceId) {
        setRealtimeData(prev => ({
          ...prev,
          tai_time: data.tai_time,
          lastUpdated: data.timestamp,
        }));
      }
    });

    // Handle ADS-B DTOE updates
    socketRef.current.on('adsb_dtoe', (data: { device_id: number; mqtt_id: string; value: number; timestamp: string; }) => {
      if (data.device_id === deviceId) {
        setRealtimeData(prev => ({
          ...prev,
          adsb_dtoe: data.value,
          lastUpdated: data.timestamp,
        }));
      }
    });

    // Handle UBX telemetry data updates
    socketRef.current.on('ubx_telemetry', (data: DeviceUbxTelemetryData) => {
      // Only update if data is for this specific device
      if (data.device_id === deviceId) {
        setRealtimeData(prev => ({
          ...prev,
          ubx_telemetry: {
            ...prev.ubx_telemetry,
            [data.gnss_device_id]: {
              ...prev.ubx_telemetry?.[data.gnss_device_id],
              [data.message_type]: {
                data: data.data,
                timestamp: data.timestamp,
              },
            },
          },
          lastUpdated: data.timestamp,
        }));
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [deviceId]);

  return { 
    realtimeData, 
    chartData,
    // Helper function to get active GNSS receivers
    getActiveGnssReceivers: () => {
      const active = [];
      if (chartData.gnss.receiver1.length > 0) active.push({ id: 1, data: chartData.gnss.receiver1, label: 'GNSS 1' });
      if (chartData.gnss.receiver2.length > 0) active.push({ id: 2, data: chartData.gnss.receiver2, label: 'GNSS 2' });
      if (chartData.gnss.receiver3.length > 0) active.push({ id: 3, data: chartData.gnss.receiver3, label: 'GNSS 3' });
      if (chartData.gnss.receiver4.length > 0) active.push({ id: 4, data: chartData.gnss.receiver4, label: 'GNSS 4' });
      return active;
    }
  };
}; 